/**
 * HC Product Grid-3 Gallery — Hero display + thumbnail grid
 *
 * Desktop: separate hero display element shows the active image full-width.
 *          All slides remain as thumbnails in a fixed grid below.
 *          Clicking a thumbnail updates the hero (no grid reflow).
 *          Clicking the hero opens PhotoSwipe.
 *
 * Mobile:  hero display is hidden; fader + thumbs strip handles navigation.
 *          Slides container DOM is NOT modified to preserve fader behavior.
 *
 * Sync strategy: MutationObserver on product-images[data-active-media]
 *   is the single source of truth. All UI updates (dots, active thumb,
 *   hero display) flow from attribute changes — regardless of trigger
 *   (thumb click, variant change, swipe, dot click).
 *
 * @version 3.2.0
 */

(function () {
  'use strict';

  var INITIALIZED_CLASS = 'hc-grid3-initialized';
  var LAYOUT_SELECTOR = '.product__wrapper--grid-3';
  var SLIDE_SELECTOR = '.product__slide';
  var HIDDEN_CLASS = 'hc-gallery-hidden';
  var ACTIVE_CLASS = 'hc-grid3-active';
  var HERO_DISPLAY_CLASS = 'hc-gallery-hero-display';
  var MOBILE_BP = 749;

  /* ── Hero display ─────────────────────────────────────────── */

  function createHeroDisplay(slidesContainer) {
    var el = document.createElement('div');
    el.className = HERO_DISPLAY_CLASS;
    // Insert BEFORE slidesContainer as a sibling, NOT inside it.
    slidesContainer.parentNode.insertBefore(el, slidesContainer);
    return el;
  }

  function updateHeroDisplay(heroDisplay, sourceSlide, zoomContainer) {
    if (!sourceSlide) return;
    var photo = sourceSlide.querySelector('.product__photo');
    if (!photo) return;

    heroDisplay.innerHTML = '';
    var clone = photo.cloneNode(true);
    clone.removeAttribute('data-zoom-image');
    var imgs = clone.querySelectorAll('img');
    imgs.forEach(function (img) { img.loading = 'eager'; });
    heroDisplay.appendChild(clone);

    // Store zoom index for PhotoSwipe
    if (zoomContainer && typeof zoomContainer.querySelectorAll === 'function') {
      var allZoom = Array.from(zoomContainer.querySelectorAll('[data-zoom-image]'));
      heroDisplay._zoomIndex = allZoom.indexOf(photo);
    }
  }

  /* ── Mobile pagination dots ───────────────────────────────── */

  function buildDots(wrapper, slidesContainer) {
    if (window.innerWidth > MOBILE_BP) return;

    var existing = wrapper.querySelector('.product__pagination');
    if (existing) existing.remove();

    var visibleSlides = slidesContainer.querySelectorAll(
      SLIDE_SELECTOR + ':not(.' + HIDDEN_CLASS + ')'
    );
    if (visibleSlides.length <= 1) return;

    var pagination = document.createElement('div');
    pagination.className = 'product__pagination';

    for (var i = 0; i < visibleSlides.length; i++) {
      var dot = document.createElement('span');
      dot.className = 'dot';
      dot.dataset.index = i;
      dot.dataset.mediaId = visibleSlides[i].dataset.mediaId || '';
      pagination.appendChild(dot);
    }

    var thumbs = wrapper.querySelector('.product__thumbs');
    if (thumbs) {
      thumbs.after(pagination);
    } else {
      var productImages = wrapper.querySelector('product-images');
      if (productImages) productImages.after(pagination);
    }

    // Dot click → update active media
    pagination.addEventListener('click', function (e) {
      var dotEl = e.target.closest('.dot');
      if (!dotEl) return;
      var mediaId = dotEl.dataset.mediaId;
      if (!mediaId) return;

      var productImagesEl = wrapper.querySelector('product-images');
      if (productImagesEl) {
        productImagesEl.dispatchEvent(
          new CustomEvent('theme:media:select', {
            bubbles: true,
            detail: { id: mediaId },
          })
        );
      }
    });

    return pagination;
  }

  function updateDots(wrapper, activeMediaId) {
    var dots = wrapper.querySelectorAll('.product__pagination .dot');
    dots.forEach(function (dot) {
      dot.classList.toggle('is-active', dot.dataset.mediaId === activeMediaId);
    });
  }

  /* ── Sync all UI from active media ID ─────────────────────── */

  function syncFromMediaId(mediaId, ctx) {
    if (!mediaId) return;

    var targetSlide = ctx.slidesContainer.querySelector(
      '[data-media-id="' + mediaId + '"]'
    );
    if (targetSlide && !targetSlide.classList.contains(HIDDEN_CLASS)) {
      ctx.slides.forEach(function (s) { s.classList.remove(ACTIVE_CLASS); });
      targetSlide.classList.add(ACTIVE_CLASS);

      if (window.innerWidth > MOBILE_BP && ctx.heroDisplay) {
        updateHeroDisplay(ctx.heroDisplay, targetSlide, ctx.slidesContainer);
      }
    }

    updateDots(ctx.wrapper, mediaId);
  }

  /* ── Initialization ───────────────────────────────────────── */

  function initWrapper(wrapper) {
    var slidesContainer = wrapper.querySelector('.product__slides');
    if (!slidesContainer) return;

    var slides = Array.from(
      slidesContainer.querySelectorAll(SLIDE_SELECTOR + ':not(.' + HIDDEN_CLASS + ')')
    );
    if (slides.length === 0) return;

    // Create hero display element OUTSIDE slidesContainer (sibling)
    var heroDisplay = createHeroDisplay(slidesContainer);

    // Shared context for sync function
    var ctx = {
      wrapper: wrapper,
      slidesContainer: slidesContainer,
      slides: slides,
      heroDisplay: heroDisplay,
    };

    // Determine initial active slide
    var productImagesEl = wrapper.querySelector('product-images');
    var initialMediaId = productImagesEl
      ? productImagesEl.getAttribute('data-active-media')
      : null;
    var activeSlide = null;

    if (initialMediaId) {
      activeSlide = slidesContainer.querySelector(
        '[data-media-id="' + initialMediaId + '"]'
      );
    }
    if (!activeSlide) {
      activeSlide = slidesContainer.querySelector('.media--active') || slides[0];
    }

    activeSlide.classList.add(ACTIVE_CLASS);
    updateHeroDisplay(heroDisplay, activeSlide, slidesContainer);
    wrapper.classList.add(INITIALIZED_CLASS);

    // ── Desktop: thumbnail click → update hero ──────────────
    slidesContainer.addEventListener('click', function (e) {
      var slide = e.target.closest(SLIDE_SELECTOR);
      if (!slide || slide.classList.contains(HIDDEN_CLASS)) return;

      // Desktop only: intercept click, prevent PhotoSwipe on thumbnails
      if (window.innerWidth > MOBILE_BP) {
        e.stopPropagation();
        e.preventDefault();

        slides.forEach(function (s) { s.classList.remove(ACTIVE_CLASS); });
        slide.classList.add(ACTIVE_CLASS);
        updateHeroDisplay(heroDisplay, slide, slidesContainer);

        // Update product-images active-media → triggers MutationObserver
        var mediaId = slide.dataset.mediaId;
        if (mediaId && productImagesEl) {
          productImagesEl.setAttribute('data-active-media', mediaId);
        }
      }
    }, true); // capture phase — fires before zoom.js

    // ── Hero display click → open PhotoSwipe ────────────────
    heroDisplay.addEventListener('click', function () {
      if (typeof slidesContainer.createZoom === 'function' && heroDisplay._zoomIndex >= 0) {
        slidesContainer.createZoom(heroDisplay._zoomIndex);
      }
    });

    // ── MutationObserver: single source of truth for sync ───
    // Observes data-active-media on product-images.
    // Fires on: thumb click, variant change, swipe, dot click.
    if (productImagesEl) {
      var observer = new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          if (mutations[i].attributeName === 'data-active-media') {
            var mediaId = productImagesEl.getAttribute('data-active-media');
            syncFromMediaId(mediaId, ctx);
          }
        }
      });
      observer.observe(productImagesEl, { attributes: true });
    }

    // Build mobile pagination dots
    buildDots(wrapper, slidesContainer);

    // Set initial dot state
    if (initialMediaId) {
      updateDots(wrapper, initialMediaId);
    } else if (activeSlide) {
      updateDots(wrapper, activeSlide.dataset.mediaId || '');
    }
  }

  function init() {
    document.querySelectorAll(LAYOUT_SELECTOR + ':not(.' + INITIALIZED_CLASS + ')').forEach(initWrapper);
  }

  window.hcProductGrid3Init = init;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
